import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReducersTypes } from "@/redux/reducers/Models";
import { validatePinAvulso } from "@/redux/ducks/customer";
import { NavigateFunction } from "react-router-dom";

const pinScheme = z.object({
  pin: z.string().refine((val) => val.length === 5, "Pin inválido"),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  error: boolean;
};

export default function usePinFormAvulso(navigate: NavigateFunction) {
  const dispatch = useDispatch();
  const user = useSelector((state: ReducersTypes) => state.userReducer);

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    error: false,
  });

  const unifiedSchema = pinScheme;
  type FormData = z.infer<typeof unifiedSchema>;

  const defaultValues: FormData = { pin: "" };

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onSubmit",
    defaultValues,
  });

  const toggleModal = useCallback(() => {
    setUi((prev) => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const handleValidatePin = useCallback(
    (success: boolean, code?: string) => {
      setUi((prev) => ({ ...prev, loading: false }));
      if (!success) {
        setUi((prev) => ({ ...prev, error: true }));
        if (code === "plate") {
          setUi((prev) => ({ ...prev, modalOpen: true }));
        }
        return;
      }
      navigate("/home", { state: { skipBackdrops: true } });
      reset();
    },
    [reset, navigate],
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;
      setUi((prev) => ({ ...prev, loading: true }));
      setUi((prev) => ({ ...prev, error: false }));
      dispatch(
        validatePinAvulso({
          pin: data.pin,
          plate: user?.plate as string,
          callback: handleValidatePin,
        }),
      );
    },
    [ui.loading, dispatch, user, handleValidatePin],
  );

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    isLoading: ui.loading,
    error: ui.error,
    isDisabled: !isValid || ui.loading,
    register,
    setValue,
    handleSubmit: handleSubmit(onSubmit),
    errors,
  };
}
